import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';
import { BRAND, SERVICES } from '@/data/content';
import { BBBSeal, TrustLine } from '@/components/site/SiteBits';
import { useAuth } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;

const TIMELINES = [
  'As soon as possible',
  'Within 2–4 weeks',
  'Within 1–3 months',
  'Just gathering estimates',
  'Flexible',
];

const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone call' },
  { value: 'text', label: 'Text message' },
];

const initialForm = {
  name: '',
  phone: '',
  email: '',
  address_or_city: '',
  service_needed: SERVICES[0].title,
  project_description: '',
  preferred_timeline: TIMELINES[0],
  best_contact: 'email',
};

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="font-sans text-xs uppercase tracking-[0.18em] text-brand-charcoal">{label}{required && <span className="text-brand-maroon"> *</span>}</span>
      <div className="mt-2">{children}</div>
      {hint && <span className="mt-1.5 block font-sans text-xs text-brand-muted">{hint}</span>}
    </label>
  );
}

export default function EstimateForm({ testIdPrefix = 'estimate' }) {
  const { user } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Prefill name/email for signed-in customers
  useEffect(() => {
    if (user && typeof user === 'object') {
      setForm((f) => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
    }
  }, [user]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length > MAX_PHOTOS) {
      toast.error(`You can upload up to ${MAX_PHOTOS} photos.`);
      setPhotos(list.slice(0, MAX_PHOTOS));
      return;
    }
    setPhotos(list);
  };

  const removePhoto = (idx) => setPhotos((ps) => ps.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address_or_city || !form.project_description) {
      toast.error('Please complete the required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((f) => fd.append('photos', f));
      const res = await axios.post(`${API}/estimates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(res.data);
      setForm(initialForm);
      setPhotos([]);
      toast.success('Estimate request sent. We\'ll contact you within 24 hours.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again or call us.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-brand-bone border border-brand-border p-6 lg:p-8" data-testid={`${testIdPrefix}-success`}>
        <Icons.CheckCircle2 size={40} strokeWidth={1.5} className="text-brand-maroon" />
        <h3 className="font-serif text-2xl mt-4 text-brand-charcoal">Thanks — request received.</h3>
        <p className="mt-3 font-sans text-sm text-brand-muted">{success.message} If your project is time-sensitive, call {BRAND.phoneDisplay}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="bg-brand-bone border border-brand-border p-6 lg:p-8 space-y-5" data-testid={`${testIdPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Name" required>
          <input required value={form.name} onChange={onChange('name')} className="hoc-input" data-testid={`${testIdPrefix}-input-name`} />
        </Field>
        <Field label="Phone" required>
          <input required type="tel" value={form.phone} onChange={onChange('phone')} className="hoc-input" data-testid={`${testIdPrefix}-input-phone`} />
        </Field>
      </div>
      <Field label="Email" required>
        <input required type="email" value={form.email} onChange={onChange('email')} className="hoc-input" data-testid={`${testIdPrefix}-input-email`} />
      </Field>
      <Field label="Project address or city" required>
        <input required value={form.address_or_city} onChange={onChange('address_or_city')} placeholder="e.g. 123 Main St, Burleson TX" className="hoc-input" data-testid={`${testIdPrefix}-input-address`} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Service needed" required>
          <select value={form.service_needed} onChange={onChange('service_needed')} className="hoc-input" data-testid={`${testIdPrefix}-input-service`}>
            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="Other / not sure">Other / not sure</option>
          </select>
        </Field>
        <Field label="Preferred timeline">
          <select value={form.preferred_timeline} onChange={onChange('preferred_timeline')} className="hoc-input" data-testid={`${testIdPrefix}-input-timeline`}>
            {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Project description" required hint="A few sentences is plenty — what you want done and any context.">
        <textarea required rows={5} value={form.project_description} onChange={onChange('project_description')} className="hoc-input" data-testid={`${testIdPrefix}-input-description`} />
      </Field>
      <Field label="Best way to contact you">
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-contact-methods`}>
          {CONTACT_METHODS.map((m) => (
            <label key={m.value} className={`px-4 py-2 border cursor-pointer text-sm font-sans transition-colors ${form.best_contact === m.value ? 'bg-brand-charcoal text-brand-bone border-brand-charcoal' : 'bg-brand-bone text-brand-charcoal border-brand-border hover:border-brand-charcoal'}`}>
              <input type="radio" name="best_contact" value={m.value} checked={form.best_contact === m.value} onChange={onChange('best_contact')} className="sr-only" />
              {m.label}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Photos (optional)" hint={`Up to ${MAX_PHOTOS} images · helps us understand scope`}>
        <label className="flex items-center gap-3 p-4 border border-dashed border-brand-border cursor-pointer hover:border-brand-charcoal transition-colors" data-testid={`${testIdPrefix}-file-input-label`}>
          <Icons.Upload size={18} className="text-brand-maroon" />
          <span className="font-sans text-sm text-brand-muted">Click to select photos</span>
          <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
        </label>
        {photos.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid={`${testIdPrefix}-photo-list`}>
            {photos.map((f, i) => (
              <li key={i} className="flex items-center justify-between border border-brand-border bg-brand-boneAlt px-3 py-1.5 text-xs font-sans">
                <span className="truncate mr-3">{f.name} · {(f.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => removePhoto(i)} className="text-brand-maroon hover:underline" aria-label="Remove photo">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </Field>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full disabled:opacity-60"
        data-testid={`${testIdPrefix}-form-submit`}
      >
        {submitting ? 'Sending…' : 'Send Estimate Request'}
      </button>
      <p className="font-sans text-xs text-brand-muted text-center">
        We reply within 24 hours. If you prefer, call {BRAND.phoneDisplay}.
      </p>
      <style>{`.hoc-input{width:100%;background:#F7F6F3;border:1px solid rgba(28,28,29,0.2);padding:0.7rem 0.85rem;font-family:Manrope,ui-sans-serif,system-ui,sans-serif;font-size:1rem;color:#1C1C1D;border-radius:0;transition:border-color .15s ease,box-shadow .15s ease}.hoc-input:focus{outline:none;border-color:#7A1E24;box-shadow:0 0 0 1px #7A1E24}`}</style>
    </form>
  );
}

// Full page/section with copy on the left and the form on the right — reusable across Home and /estimate.
export function EstimateSection({ testIdPrefix = 'estimate', anchorId, heading = 'Tell us about your project.', headingClass = '', eyebrow = 'Request an Estimate', headingTag: HeadingTag = 'h2' }) {
  return (
    <section id={anchorId} className="border-b border-brand-border bg-brand-bone" data-testid={`${testIdPrefix}-section`}>
      <div className="container-x py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12">
        <div>
          <div className="eyebrow mb-3">{eyebrow}</div>
          <HeadingTag className={`font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight ${headingClass || 'text-brand-charcoal'}`}>{heading}</HeadingTag>
          <p className="body-lg mt-6">
            Share the details — service needed, timeline, and a short description. Include photos and measurements if you have them. We'll review the scope, discuss options, and follow up <strong className="text-brand-charcoal">within 24 hours</strong>.
          </p>

          <div className="mt-10 border-t border-brand-border pt-8 space-y-5 font-sans text-sm text-brand-charcoal">
            <div className="flex items-start gap-3">
              <Icons.Phone size={16} className="text-brand-maroon mt-0.5" />
              <div>
                <div className="uppercase tracking-wider text-xs text-brand-muted">Phone</div>
                <a href={BRAND.phoneHref} className="text-base hover:text-brand-maroon">{BRAND.phoneDisplay} · ({BRAND.phoneNumeric})</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icons.Mail size={16} className="text-brand-maroon mt-0.5" />
              <div>
                <div className="uppercase tracking-wider text-xs text-brand-muted">Email</div>
                <a href={BRAND.emailHref} className="text-base hover:text-brand-maroon break-all">{BRAND.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icons.MapPin size={16} className="text-brand-maroon mt-0.5" />
              <div>
                <div className="uppercase tracking-wider text-xs text-brand-muted">Service area</div>
                <div className="text-base">Fort Worth · Burleson · Weatherford · Granbury · Cleburne · DFW</div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-brand-border flex items-center gap-4">
            <BBBSeal size="sm" testId={`${testIdPrefix}-bbb-seal`} />
            <TrustLine items={['BBB Accredited', 'Local DFW company', 'Craftsmanship-focused work', 'Clear communication']} className="text-xs" />
          </div>
        </div>

        <EstimateForm testIdPrefix={testIdPrefix} />
      </div>
    </section>
  );
}
